import { ExternalLink, Grid2x2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  EVOHOME_FAMILY_LABEL,
  EVOHOME_FAMILY_SIGNIN_NOTE,
  EVOHOME_FAMILY_SITES,
} from "@/config/evohomeFamily";

interface Props {
  /** Compact variant for the slim header utility bar. */
  compact?: boolean;
  className?: string;
}

/**
 * Family switcher used in the header, mobile menu and account area.
 *
 * Works identically signed in and signed out: each sibling brand simply opens
 * its own production website in a new tab. It never implies a shared session —
 * the note makes the separate sign-in explicit.
 */
export default function EvoHomeFamilySwitcher({ compact = false, className = "" }: Props) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={`${compact ? "h-7 px-2 text-[11px]" : "min-h-[44px]"} gap-1.5 font-medium ${className}`}
          aria-label="Switch to another EvoHome website"
        >
          <Grid2x2 className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} aria-hidden="true" />
          EvoHome family
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 bg-popover z-50">
        <DropdownMenuLabel>{EVOHOME_FAMILY_LABEL}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {EVOHOME_FAMILY_SITES.map((site) => (
          <DropdownMenuItem key={site.key} asChild className="cursor-pointer">
            <a
              href={site.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${site.name} (opens in a new tab)`}
              className="flex flex-col items-start gap-0.5 py-2"
            >
              <span className="flex items-center gap-1.5 text-sm font-medium">
                {site.name}
                {site.isCurrent ? (
                  <span className="text-[10px] text-muted-foreground">(this site)</span>
                ) : (
                  <ExternalLink className="w-3 h-3" aria-hidden="true" />
                )}
              </span>
              <span className="text-xs text-muted-foreground">{site.description}</span>
            </a>
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <p className="px-2 py-2 text-[11px] leading-relaxed text-muted-foreground">
          {EVOHOME_FAMILY_SIGNIN_NOTE}
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
